import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { schemaParserService } from '../../services/api/schemaParserService';

const ResizableTableCell = ({ children, width, onResize, ...props }) => (
  <TableCell
    {...props}
    sx={{
      width: width,
      minWidth: width,
      maxWidth: width,
      position: 'relative',
      padding: '8px',
      ...props.sx
    }}
  >
    {children}
    {onResize && (
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '5px',
          cursor: 'col-resize',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3
          }
        }}
      />
    )}
  </TableCell>
);

const SchemaManager = () => {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [resultDialog, setResultDialog] = useState({ open: false, result: null });
  const [error, setError] = useState(null);
  const [columnWidths, setColumnWidths] = useState({
    schemaName: 250,
    tags: 100,
    lastGenerated: 200,
    actions: 150
  });
  const [resizing, setResizing] = useState(null);

  useEffect(() => {
    loadSchemas();
  }, []);

  const loadSchemas = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await schemaParserService.listSchemas();
      setSchemas(response.schemas || []);
    } catch (err) {
      console.error('Failed to load schemas:', err);
      setError('Failed to load schemas: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async (schemaName) => {
    if (!confirm(`Regenerate tags from ${schemaName}?\n\nThis will update all tags defined in this schema.`)) {
      return;
    }

    setRegenerating(schemaName);
    setError(null);

    try {
      const response = await schemaParserService.generateTags(schemaName);

      setResultDialog({
        open: true,
        result: {
          success: true,
          schema_name: schemaName,
          tags_generated: response.tags_generated,
          stats: response.stats
        }
      });

      // Refresh schemas list
      await loadSchemas();

    } catch (err) {
      console.error(`Failed to generate tags from ${schemaName}:`, err);
      setResultDialog({
        open: true,
        result: {
          success: false,
          schema_name: schemaName,
          error: err.message
        }
      });
    } finally {
      setRegenerating(null);
    }
  };

  const handleRegenerateAll = async () => {
    if (!confirm(
      'Regenerate tags from ALL schemas?\n\n' +
      'This will process all schemas and update the tag registry.\n' +
      'This may take a moment.'
    )) {
      return;
    }

    setRegenerating('all');
    setError(null);

    try {
      const response = await schemaParserService.generateAllTags();

      setResultDialog({
        open: true,
        result: {
          success: true,
          all: true,
          schemas_processed: response.schemas_processed,
          total_tags: response.total_tags,
          results: response.results
        }
      });

      // Refresh schemas list
      await loadSchemas();

    } catch (err) {
      console.error('Failed to generate all tags:', err);
      setResultDialog({
        open: true,
        result: {
          success: false,
          all: true,
          error: err.message
        }
      });
    } finally {
      setRegenerating(null);
    }
  };

  const handleCloseResultDialog = () => {
    setResultDialog({ open: false, result: null });
  };

  const handleMouseDown = (columnKey) => (e) => {
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Schema Registry</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSchemas}
            disabled={loading || regenerating !== null}
          >
            Refresh List
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRegenerateAll}
            disabled={loading || regenerating !== null || schemas.length === 0}
          >
            {regenerating === 'all' ? 'Regenerating...' : 'Regenerate All Schemas'}
          </Button>
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Schema-Driven Tag Generation:</strong> Upload .schema.json files to S3, then click
          "Regenerate" to update tags. Tags are automatically created/updated based on schema definitions.
        </Typography>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          S3 Bucket: <code>{schemas[0]?.bucket || 'Loading...'}</code>
        </Typography>
      </Alert>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Schemas Table */}
      <TableContainer component={Paper}>
        <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <ResizableTableCell width={columnWidths.schemaName} onResize={handleMouseDown('schemaName')}>
                Schema Name
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.tags} onResize={handleMouseDown('tags')} align="center">
                Tags
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.lastGenerated} onResize={handleMouseDown('lastGenerated')}>
                Last Generated
              </ResizableTableCell>
              <ResizableTableCell width={columnWidths.actions} align="right">
                Actions
              </ResizableTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schemas.length === 0 && !loading && (
              <TableRow>
                <ResizableTableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No schemas found. Upload .schema.json files to S3 bucket: schemas/
                  </Typography>
                </ResizableTableCell>
              </TableRow>
            )}

            {schemas.map((schema) => (
              <TableRow key={schema.name} hover>
                <ResizableTableCell width={columnWidths.schemaName}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {schema.name}
                  </Typography>
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.tags} align="center">
                  <Chip
                    label={schema.tag_count || 0}
                    color={schema.tag_count > 0 ? 'primary' : 'default'}
                    size="small"
                  />
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.lastGenerated}>
                  {schema.last_generated ? (
                    <Typography variant="body2">
                      {new Date(schema.last_generated).toLocaleString()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Never
                    </Typography>
                  )}
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.actions} align="right">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      regenerating === schema.name ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                    onClick={() => handleRegenerate(schema.name)}
                    disabled={regenerating !== null}
                  >
                    {regenerating === schema.name ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                </ResizableTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Total Count */}
      {schemas.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="h5">
            {schemas.reduce((sum, s) => sum + (s.tag_count || 0), 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total tags from {schemas.length} schema{schemas.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Result Dialog */}
      <Dialog
        open={resultDialog.open}
        onClose={handleCloseResultDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {resultDialog.result?.success ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" />
              <span>Tags Generated Successfully</span>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ErrorIcon color="error" />
              <span>Generation Failed</span>
            </Box>
          )}
        </DialogTitle>

        <DialogContent>
          {resultDialog.result?.success && !resultDialog.result?.all && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Schema:</strong> {resultDialog.result.schema_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Tags Generated:</strong> {resultDialog.result.tags_generated}
              </Typography>

              {resultDialog.result.stats && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Created: {resultDialog.result.stats.created} •
                    Updated: {resultDialog.result.stats.updated} •
                    Skipped: {resultDialog.result.stats.skipped}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {resultDialog.result?.success && resultDialog.result?.all && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Schemas Processed:</strong> {resultDialog.result.schemas_processed}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Total Tags:</strong> {resultDialog.result.total_tags}
              </Typography>

              {resultDialog.result.results && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Details:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {resultDialog.result.results.map((res, idx) => (
                      <Box key={idx} sx={{ py: 0.5 }}>
                        <Typography variant="body2">
                          {res.status === 'success' && (
                            <CheckCircleIcon fontSize="small" color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />
                          )}
                          {res.status === 'error' && (
                            <ErrorIcon fontSize="small" color="error" sx={{ verticalAlign: 'middle', mr: 1 }} />
                          )}
                          {res.schema_name}: {res.tags_generated || 0} tags
                          {res.status === 'skipped' && ' (skipped)'}
                          {res.status === 'error' && ` - ${res.error}`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {!resultDialog.result?.success && (
            <Alert severity="error">
              {resultDialog.result?.error || 'Unknown error occurred'}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseResultDialog} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchemaManager;
